// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import { Activity, Beaker, Building2, FileSignature, FileText, Inbox, KeyRound, LayoutList, Link2, Lock, ScrollText, Settings, Share2, ShieldCheck, Tablets, Truck } from 'lucide-react'
import { useAuth, usePmsName } from '../../auth/AuthContext'
import { useModules } from '../../auth/ModulesContext'
import ModuleLocked from '../../components/ModuleLocked'
import MyPharmacySettings from './MyPharmacySettings'
import SecuritySettings from './SecuritySettings'
import AuditLogSettings from './AuditLogSettings'
import ViewBuilderPage from './ViewBuilderPage'
import CustomFieldsTab from './CustomFieldsTab'
import DrugReferencePage from '../pharmacy/DrugReferencePage'
import SiteOfCareTab from './SiteOfCareTab'
import LabProvidersTab from './LabProvidersTab'
import PioneerRxTab from '../admin/PioneerRxTab'
import PmsIntegrationTab from './PmsIntegrationTab'
import ShippingSettingsTab from './ShippingSettingsTab'
import DataApiSettingsTab from './DataApiSettingsTab'
import EsignConfigPage from '../carerx/EsignConfigPage'
import DocumentsTab from './DocumentsTab'
import SecureShareAccessTab from './SecureShareAccessTab'

// Lazy-load the admin pages (they already exist at /admin/*)
import RolesTab from '../admin/RolesTab'
import InvitationsTab from '../admin/InvitationsTab'

interface Tab {
  key: string
  label: string
  icon: LucideIcon
  permission?: string
  /** ADR-019 D-9: opt-in module gate; tab hidden if the pharmacy isn't entitled. */
  module?: string
  component: React.ComponentType
}

const TABS: Tab[] = [
  { key: 'pharmacy', label: 'Profile', icon: Settings, permission: 'pharmacy.self.manage', component: MyPharmacySettings },
  { key: 'security', label: 'Security', icon: Lock, component: SecuritySettings },
  { key: 'documents', label: 'Documents', icon: FileText, permission: 'patient.docs.read', component: DocumentsTab },
  { key: 'secure-share', label: 'Secure Share', icon: Share2, permission: 'pharmacy.self.manage', module: 'secure_share', component: SecureShareAccessTab },
  { key: 'roles',    label: 'Roles & permissions', icon: ShieldCheck, permission: 'admin.roles.read', component: RolesTab },
  { key: 'invites',  label: 'Invitations', icon: Inbox, permission: 'admin.users.invite', component: InvitationsTab },
  { key: 'audit',    label: 'Audit log', icon: ScrollText, permission: 'admin.audit.read', component: () => <AuditLogSettings /> },
  { key: 'views',    label: 'Custom views', icon: LayoutList, permission: 'rx.read', module: 'queues', component: ViewBuilderPage },
  { key: 'fields',   label: 'Custom fields', icon: LayoutList, permission: 'admin.users.read', module: 'queues', component: CustomFieldsTab },
  { key: 'drug-reference', label: 'DrugReference', icon: Tablets, permission: 'rx.read', module: 'drugreference', component: () => <DrugReferencePage /> },
  { key: 'site-of-care', label: 'Site of Care', icon: Building2, permission: 'rx.read', module: 'carerx', component: SiteOfCareTab },
  { key: 'lab-providers', label: 'Lab providers', icon: Beaker, permission: 'pharmacy.self.manage', module: 'carerx', component: LabProvidersTab },
  { key: 'pms', label: 'PMS integration', icon: Link2, permission: 'pharmacy.self.manage', component: PmsIntegrationTab },
  { key: 'pioneerrx', label: 'PMS events', icon: Activity, permission: 'admin.users.read', component: PioneerRxTab },
  { key: 'shipping', label: 'Shipping', icon: Truck, permission: 'shipping.config', module: 'shipping', component: ShippingSettingsTab },
  { key: 'data-api', label: 'Data API', icon: KeyRound, permission: 'data_api.manage', module: 'data_api', component: DataApiSettingsTab },
  { key: 'esign',    label: 'E-signature', icon: FileSignature, permission: 'admin.users.read', module: 'carerx', component: EsignConfigPage },
]

export default function PharmacySettingsHub() {
  const { hasPermission } = useAuth()
  const { isDisabled: isModuleDisabled } = useModules()
  const [params] = useSearchParams()
  // Keep module-gated tabs VISIBLE (show-locked) — only permission/role still hides a tab.
  const { name: pmsName, known: pmsKnown } = usePmsName()
  // D-PMS-1: the raw-events tab is named after the tenant's OWN dispensing system.
  // It read "PioneerRx events" for every pharmacy, including ones that run
  // something else — the tab shows whatever the connected PMS sends.
  const visibleTabs = TABS
    .filter(t => !t.permission || hasPermission(t.permission))
    .map(t => t.key === 'pioneerrx'
      ? { ...t, label: pmsKnown ? `${pmsName.replace(/ Software$/, '')} events` : 'PMS events' }
      : t)
  const tabLocked = (t: Tab) => !!(t.module && isModuleDisabled(t.module))
  const [active, setActive] = useState(() => {
    const requested = params.get('tab')
    return (requested && visibleTabs.some(t => t.key === requested)) ? requested : (visibleTabs[0]?.key ?? 'pharmacy')
  })
  const activeTab = visibleTabs.find(t => t.key === active)
  const ActiveComponent = activeTab?.component ?? (() => null)

  return (
    <div style={{ maxWidth: '100%', margin: 0, padding: '16px 24px' }}>
      {/* Tab bar */}
      {/* Tab bar: full-width, sticky, horizontal-scroll only (overflowY hidden kills the stray
          vertical scroll arrows caused by the -1px active-border overlap), stable height. */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--color-border)', marginBottom: 20,
                    overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'thin', minHeight: 42,
                    position: 'sticky', top: 0, zIndex: 5, background: 'var(--color-bg, #fafaf7)' }}>
        {visibleTabs.map(tab => {
          const Icon = tab.icon
          const isActive = tab.key === active
          const locked = tabLocked(tab)
          return (
            <button
              key={tab.key}
              onClick={() => setActive(tab.key)}
              title={locked ? 'Not included in your plan — contact your account executive for licensing' : undefined}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 16px', fontSize: 13, fontWeight: isActive ? 600 : 400,
                color: isActive ? 'var(--color-accent, #2563eb)' : 'var(--color-text-soft)',
                background: 'transparent', border: 'none', borderBottom: isActive ? '2px solid var(--color-accent, #2563eb)' : '2px solid transparent',
                cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                marginBottom: -1,
                opacity: locked ? 0.6 : 1,
              }}
            >
              <Icon size={15} /> {tab.label}
              {locked && <Lock size={12} aria-hidden="true" />}
            </button>
          )
        })}
      </div>

      {/* Active tab content */}
      {activeTab && tabLocked(activeTab)
        ? <ModuleLocked moduleKey={activeTab.module} compact />
        : <ActiveComponent />}
    </div>
  )
}
